import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Spin, Result, Image, Carousel, Row, Col, Typography, Tag, Divider, Space, Statistic, Table, Select } from 'antd';
import JsBarcode from 'jsbarcode';
import { 
  FileImageOutlined, 
  CalendarOutlined, 
  DollarOutlined, 
  UserOutlined, 
  EnvironmentOutlined,
  TagOutlined,
  ToolOutlined,
  InfoCircleOutlined,
  AppstoreAddOutlined,
  ShoppingOutlined,
  HistoryOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// Style constants
const cardStyle: React.CSSProperties = { borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' };
const cardTitleStyle: React.CSSProperties = { marginBottom: 8 };
const infoRowStyle: React.CSSProperties = { padding: '4px 0' };
const infoLabelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600 };
const infoValueStyle: React.CSSProperties = { fontSize: 13 };

interface PublicAssetDetailProps {
  public_uuid?: string;
  isModal?: boolean;
}

const PublicAssetDetail: React.FC<PublicAssetDetailProps> = ({ public_uuid: propUuid, isModal }) => {
  const params = useParams();
  const public_uuid = propUuid || params.public_uuid;
  const [asset, setAsset] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [barcodeDataUrl, setBarcodeDataUrl] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string | undefined>();
  const [histPageSize,setHistPageSize]=useState(10);


  useEffect(() => {
    setLoading(true);
    fetch(`/api/assets/public/${public_uuid}`)
      .then(res => res.json())
      .then(assetData => {
        setAsset(assetData);
        setLoading(false);
        if (assetData && assetData.asset_code) {
          try {
            const canvas = document.createElement('canvas');
            JsBarcode(canvas, assetData.asset_code, {
              format: 'CODE128', width: 5, height: 200, fontSize: 32, margin: 10, displayValue: true, background: 'transparent', lineColor: '#000',
            });
            setBarcodeDataUrl(canvas.toDataURL('image/png'));
          } catch {}
        }
      })
      .catch(() => setLoading(false));
  }, [public_uuid]);

  const getStatusName = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'pending_deployment': 'Chờ triển khai', 'in_use': 'Đang sử dụng', 'in_storage': 'Đang lưu kho', 'under_maintenance': 'Đang bảo dưỡng', 'disposed': 'Đã thanh lý'
    };
    return statusMap[status] || status || '—';
  };
  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'pending_deployment': 'orange', 'in_use': 'green', 'in_storage': 'blue', 'under_maintenance': 'purple', 'disposed': 'red'
    };
    return colorMap[status] || 'default';
  };
  const formatCurrency = (amount: number) => {
    if (!amount) return '—';
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' VNĐ';
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  // Render 1 row thông tin nhanh
  const renderQuickInfoRow = (label: React.ReactNode, value: React.ReactNode) => (
    <div style={infoRowStyle}>
      <Text strong style={infoLabelStyle}>{label}</Text>
      <br />
      <Text style={infoValueStyle}>{value}</Text>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!asset || asset.message) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Result status="404" title="Không tìm thấy tài sản" subTitle="Tài sản này có thể đã bị xóa hoặc không tồn tại" />
      </div>
    );
  }

  const imageAttachments = asset.attachments?.filter((att: any) => att.attachment_type === 'image' && att.file_name) || [];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)', padding: '20px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32, color: 'white' }}>
          <Title level={1} style={{ color: 'white', marginBottom: 8 }}>Hệ thống Quản lý Tài sản</Title>
          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16 }}>Thông tin chi tiết tài sản</Text>
        </div>
        <Row gutter={[24, 24]}>
          {/* Left Column - Asset Info */}
          <Col xs={24} lg={18}>
            <Card style={cardStyle}>
              {/* Asset Header */}
              <div style={{ marginBottom: 24 }}>
                <Row gutter={24} align="middle">
                  <Col xs={24} md={16}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                      <TagOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 12 }} />
                      <Title level={2} style={{ margin: 0, color: '#1890ff' }}>{asset.name}</Title>
                    </div>
                    <Space size="large">
                      <Text strong style={{ fontSize: 16 }}>Mã: {asset.asset_code}</Text>
                      <Tag color={getStatusColor(asset.status)} style={{ fontSize: 14, padding: '4px 12px' }}>{getStatusName(asset.status)}</Tag>
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                      {barcodeDataUrl ? (
                        <img src={barcodeDataUrl} alt={`Barcode ${asset.asset_code}`} style={{ maxWidth: '100%', height: 'auto' }} />
                      ) : (
                        <Text type="secondary">Đang tạo barcode...</Text>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
              {/* Image Carousel */}
              {imageAttachments.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  {imageAttachments.length === 1 ? (
                    <img src={`/uploads/images/${imageAttachments[0].file_name}`} alt={imageAttachments[0].description || 'Hình ảnh tài sản'} style={{ width: '100%', height: 320, objectFit: 'contain', borderRadius: 12, background: '#f5f5f5' }} />
                  ) : (
                    <Carousel dots style={{ width: '100%' }}>
                      {imageAttachments.map((att: any) => (
                        <div key={att.id}>
                          <img src={`/uploads/images/${att.file_name}`} alt={att.description || 'Hình ảnh tài sản'} style={{ width: '100%', height: 320, objectFit: 'contain', borderRadius: 12, background: '#f5f5f5' }} />
                        </div>
                      ))}
                    </Carousel>
                  )}
                </div>
              )}
              {/* Asset Details */}
              <div style={{ marginBottom: 32 }}>
                <Title level={3} style={{ marginBottom: 16 }}>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  Thông tin chi tiết
                </Title>
                <Table
                  dataSource={[
                    { label: 'Công ty', value: asset.company_name || '—' },
                    { label: 'Danh mục', value: asset.category_name || asset.category_id || '—' },
                    { label: 'Vị trí', value: asset.location_name || asset.location_id || '—' },
                    { label: 'Nhân viên', value: asset.employee_name || asset.assigned_to_employee_id || '—' },
                    { label: 'Tài sản cha', value: asset.parent_asset_name || asset.parent_asset_id || '—' },
                    { label: 'Giá trị mua', value: formatCurrency(asset.purchase_cost) },
                    { label: 'Ngày mua', value: formatDate(asset.purchase_date) },
                    { label: 'Tình trạng kỹ thuật', value: asset.technical_condition_vi || asset.technical_condition || '—' },
                    { label: 'Ngày hết hạn bảo hành', value: formatDate(asset.warranty_expiry_date) },
                    { label: 'Ngày kiểm kê gần nhất', value: formatDate(asset.last_audit_date) },
                    { label: 'Trạng thái', value: <Tag color={getStatusColor(asset.status)}>{getStatusName(asset.status)}</Tag> },
                    { label: 'Mô tả', value: asset.description || '—' },
                  ]}
                  columns={[
                    { title: 'Thông tin', dataIndex: 'label', key: 'label', width: '35%' },
                    { title: 'Giá trị', dataIndex: 'value', key: 'value', width: '65%' }
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  style={{ background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                  rowKey={(record, index) => index?.toString() || '0'}
                />
              </div>
              {/* Technical Specifications */}
              {asset.technical_specifications && Object.keys(asset.technical_specifications).length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <Title level={3} style={{ marginBottom: 16 }}>
                    <ToolOutlined style={{ marginRight: 8 }} />
                    Thông số kỹ thuật
                  </Title>
                  <Row gutter={[16, 16]}>
                    {Object.entries(asset.technical_specifications).map(([key, value]) => (
                      <Col xs={24} sm={12} key={key}>
                        <Card 
                          size="small" 
                          style={{ 
                            background: '#f8f9fa',
                            border: '1px solid #e9ecef'
                          }}
                        >
                          <Text strong style={{ color: '#1890ff' }}>{key}:</Text>
                          <br />
                          <Text>{String(value) || '—'}</Text>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
              {/* Description */}
              {asset.description && (
                <div style={{ marginBottom: 32 }}>
                  <Title level={3} style={{ marginBottom: 16 }}>
                    <InfoCircleOutlined style={{ marginRight: 8 }} />
                    Mô tả
                  </Title>
                  <Paragraph style={{ 
                    fontSize: 16, 
                    lineHeight: 1.6,
                    color: '#666'
                  }}>
                    {asset.description}
                  </Paragraph>
                </div>
              )}

              {/* Maintenance History Section (Moved) */}
              {asset.maintenance_history && asset.maintenance_history.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={3} style={{ margin: 0 }}>
                      <HistoryOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                      Lịch sử tác nghiệp
                    </Title>
                    <Space size={16}>
                      <Text strong style={{ fontSize: 16 }}>
                        Tổng chi phí: <span style={{ color: '#d4380d' }}>{formatCurrency(Math.round((asset.maintenance_history as any[]).reduce((s, r) => s + (Number(r.cost) || 0), 0)))}</span>
                      </Text>
                      <Select
                        allowClear
                        placeholder="Lọc loại tác nghiệp"
                        value={filterType}
                        onChange={setFilterType}
                        style={{ minWidth: 180 }}
                      >
                        {Array.from(new Set(asset.maintenance_history.map((m: any) => m.maintenance_type_name || m.maintenance_type_id))).map((t: any) => (
                          <Select.Option key={t} value={String(t)}>{t}</Select.Option>
                        ))}
                      </Select>
                    </Space>
                  </div>
                  <Table
                    dataSource={filterType ? asset.maintenance_history.filter((m:any)=>String(m.maintenance_type_name||m.maintenance_type_id)===filterType) : asset.maintenance_history}
                    columns={[
                      {
                        title: 'STT',
                        key: 'index',
                        width: '6%',
                        align: 'center' as const,
                        render: (_: any, __: any, idx: number) => idx + 1,
                      },
                      {
                        title: 'Ngày',
                        dataIndex: 'event_date',
                        key: 'event_date',
                        render: (_: any, record: any) => {
                          const d = record.event_date || record.completed_date || record.work_date;
                          return d ? formatDate(d) : <Text type="secondary">—</Text>;
                        },
                        width: '12%',
                        align: 'center' as const,
                      },
                      {
                        title: 'Mô tả',
                        dataIndex: 'details',
                        key: 'details',
                        render: (_: any, r: any) => {
                          const desc = r.details || r.work_description || r.description || r.notes;
                          return desc ? desc : <Text type="secondary">—</Text>;
                        },
                        width: '32%',
                      },
                      
                      {
                        title: 'Người tác nghiệp',
                        dataIndex: 'performed_by_name',
                        key: 'performed_by_name',
                        render: (_: any, r: any) => r.performed_by_name || r.performed_by_employee_name || r.employee_name || '—',
                        width: '18%',
                      },
                      {
                        title: 'Chi phí',
                        dataIndex: 'cost',
                        key: 'cost',
                        render: (cost: number) => {
                          if (!cost) return <Text type="secondary">—</Text>;
                          const rounded = Math.round(cost);
                          return <Text strong style={{ color: '#52c41a' }}>{formatCurrency(rounded)}</Text>;
                        },
                        width: '14%',
                        align: 'right' as const,
                      },
                    ]}
                    pagination={{
                      pageSize: histPageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ['10','20','50','100'],
                      onShowSizeChange: (_ ,size)=>setHistPageSize(size)
                    }}
                    size="small"
                    style={{
                      background: 'white',
                      borderRadius: 8,
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                    rowKey={(record, index) => index?.toString() || '0'}
                  />
                </div>
              )}

              {/* Accessories Section */}
              {asset.accessories && asset.accessories.length > 0 && (
                <div>
                  <Title level={3} style={{ marginBottom: 16 }}>
                    <AppstoreAddOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                    Thiết bị/Vật tư kèm theo
                  </Title>
                  
                  {/* Accessories Table */}
                  <Table
                    dataSource={asset.accessories}
                    columns={[
                      {
                        title: 'Tên thiết bị',
                        dataIndex: 'name',
                        key: 'name',
                        render: (text: string) => (
                          <Text strong style={{ color: '#1890ff', fontSize: 14 }}>
                            {text}
                          </Text>
                        ),
                        width: '25%'
                      },
                      {
                        title: 'Số lượng',
                        dataIndex: 'quantity',
                        key: 'quantity',
                        render: (quantity: number, record: any) => (
                          <Text strong>
                            {quantity} {record.unit}
                          </Text>
                        ),
                        width: '15%',
                        align: 'center' as const
                      },
                      {
                        title: 'Đơn giá',
                        dataIndex: 'price',
                        key: 'price',
                        render: (price: number) => (
                          price ? (
                            <Text strong style={{ color: '#52c41a' }}>
                              {formatCurrency(price)}
                            </Text>
                          ) : (
                            <Text type="secondary">—</Text>
                          )
                        ),
                        width: '15%',
                        align: 'right' as const
                      },
                      {
                        title: 'Tổng giá',
                        dataIndex: 'total_price',
                        key: 'total_price',
                        render: (totalPrice: number) => (
                          totalPrice ? (
                            <Text strong style={{ color: '#fa8c16' }}>
                              {formatCurrency(totalPrice)}
                            </Text>
                          ) : (
                            <Text type="secondary">—</Text>
                          )
                        ),
                        width: '15%',
                        align: 'right' as const
                      },
                      {
                        title: 'Ngày mua',
                        dataIndex: 'purchase_date',
                        key: 'purchase_date',
                        render: (date: string) => (
                          date ? formatDate(date) : <Text type="secondary">—</Text>
                        ),
                        width: '15%',
                        align: 'center' as const
                      },
                      {
                        title: 'Hồ sơ mua',
                        dataIndex: 'purchase_document',
                        key: 'purchase_document',
                        render: (doc: string) => (
                          doc ? <Text code>{doc}</Text> : <Text type="secondary">—</Text>
                        ),
                        width: '15%'
                      }
                    ]}
                    pagination={false}
                    size="small"
                    style={{
                      background: 'white',
                      borderRadius: 8,
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                    rowKey={(record, index) => index?.toString() || '0'}
                  />
                  
                  {/* Notes Column */}
                  {asset.accessories.some((acc: any) => acc.notes) && (
                    <div style={{ marginTop: 16 }}>
                      <Title level={4} style={{ marginBottom: 12, fontSize: 16 }}>
                        Ghi chú bổ sung:
                      </Title>
                      <Table
                        dataSource={asset.accessories.filter((acc: any) => acc.notes)}
                        columns={[
                          {
                            title: 'Tên thiết bị',
                            dataIndex: 'name',
                            key: 'name',
                            render: (text: string) => (
                              <Text strong style={{ color: '#1890ff' }}>
                                {text}
                              </Text>
                            ),
                            width: '30%'
                          },
                          {
                            title: 'Ghi chú',
                            dataIndex: 'notes',
                            key: 'notes',
                            render: (notes: string) => (
                              <Text italic style={{ color: '#666' }}>
                                {notes}
                              </Text>
                            ),
                            width: '70%'
                          }
                        ]}
                        pagination={false}
                        size="small"
                        style={{
                          background: '#f8f9fa',
                          borderRadius: 8,
                          overflow: 'hidden'
                        }}
                        rowKey={(record, index) => index?.toString() || '0'}
                      />
                    </div>
                  )}

                  {/* Maintenance History Section */}
                  {asset.maintenance_history && asset.maintenance_history.length > 0 && (
                    <div style={{ marginTop: 32 }}>
                      <Title level={3} style={{ marginBottom: 16 }}>
                        <HistoryOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                        Lịch sử tác nghiệp
                      </Title>
                      
                      <Table
                        dataSource={asset.maintenance_history}
                        columns={[
                          {
                            title: 'Ngày',
                            dataIndex: 'event_date',
                            key: 'event_date',
                            render: (date: string) => (
                              date ? formatDate(date) : <Text type="secondary">—</Text>
                            ),
                            width: '15%',
                            align: 'center' as const
                          },
                          {
                            title: 'Loại tác nghiệp',
                            dataIndex: 'maintenance_type_name',
                            key: 'maintenance_type_name',
                            render: (typeName: string, record: any) => (
                              typeName ? (
                                <Text strong>{typeName}</Text>
                              ) : (
                                <Text type="secondary">{record.maintenance_type_id}</Text>
                              )
                            ),
                            width: '15%',
                            align: 'center' as const
                          },
                          {
                            title: 'Chi phí',
                            dataIndex: 'cost',
                            key: 'cost',
                            render: (cost: number) => (
                              cost ? (
                                <Text strong style={{ color: '#fa8c16' }}>
                                  {formatCurrency(cost)}
                                </Text>
                              ) : (
                                <Text type="secondary">—</Text>
                              )
                            ),
                            width: '15%',
                            align: 'right' as const
                          },
                          {
                            title: 'Nhân viên',
                            dataIndex: 'employee_name',
                            key: 'employee_name',
                            render: (employeeName: string, record: any) => (
                              employeeName ? (
                                <Text>{employeeName}</Text>
                              ) : (
                                record.performed_by_employee_id ? (
                                  <Text type="secondary">{record.performed_by_employee_id}</Text>
                                ) : (
                                  <Text type="secondary">—</Text>
                                )
                              )
                            ),
                            width: '15%'
                          },
                          {
                            title: 'Nhà cung cấp',
                            dataIndex: 'supplier_name',
                            key: 'supplier_name',
                            render: (supplierName: string, record: any) => (
                              supplierName ? (
                                <Text>{supplierName}</Text>
                              ) : (
                                record.performed_by_supplier_id ? (
                                  <Text type="secondary">{record.performed_by_supplier_id}</Text>
                                ) : (
                                  <Text type="secondary">—</Text>
                                )
                              )
                            ),
                            width: '15%'
                          },
                          {
                            title: 'Chi tiết',
                            dataIndex: 'details',
                            key: 'details',
                            render: (details: string) => (
                              details ? (
                                <Text italic style={{ color: '#666' }}>
                                  {details}
                                </Text>
                              ) : (
                                <Text type="secondary">—</Text>
                              )
                            ),
                            width: '25%'
                          }
                        ]}
                        pagination={false}
                        size="small"
                        style={{
                          background: 'white',
                          borderRadius: 8,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                        rowKey={(record, index) => index?.toString() || '0'}
                      />
                    </div>
                  )}
                </div>
              )}
            </Card>
          </Col>
          {/* Right Column - Statistics & Info */}
          <Col xs={24} lg={6}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Financial Info */}
              <Card style={cardStyle}>
                <Title level={4} style={cardTitleStyle}><DollarOutlined style={{ marginRight: 8, color: '#52c41a' }} />Thông tin tài chính</Title>
                <Statistic title="Giá trị mua" value={formatCurrency(asset.purchase_cost)} valueStyle={{ color: '#52c41a', fontSize: 24 }} />
              </Card>
              {/* Asset Status */}
              <Card style={cardStyle}>
                <Title level={4} style={cardTitleStyle}><TagOutlined style={{ marginRight: 8, color: '#1890ff' }} />Trạng thái</Title>
                <div style={{ textAlign: 'center' }}>
                  <Tag color={getStatusColor(asset.status)} style={{ fontSize: 16, padding: '8px 16px', borderRadius: 8 }}>{getStatusName(asset.status)}</Tag>
                </div>
              </Card>
              {/* Quick Info */}
              <Card style={cardStyle}>
                <Title level={4} style={cardTitleStyle}><InfoCircleOutlined style={{ marginRight: 8, color: '#722ed1' }} />Thông tin nhanh</Title>
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  {renderQuickInfoRow('Mã tài sản:', <Text code style={{ fontSize: 13 }}>{asset.asset_code}</Text>)}
                  <Divider style={{ margin: '4px 0' }} />
                  {renderQuickInfoRow('Danh mục:', <><TagOutlined style={{ color: '#52c41a', marginRight: 4 }} />{asset.category_name || asset.category_id || '—'}</>)}
                  <Divider style={{ margin: '4px 0' }} />
                  {renderQuickInfoRow('Vị trí:', <><EnvironmentOutlined style={{ color: '#1890ff', marginRight: 4 }} />{asset.location_name || asset.location_id || '—'}</>)}
                  <Divider style={{ margin: '4px 0' }} />
                  {renderQuickInfoRow('Nhân viên được giao:', <><UserOutlined style={{ color: '#722ed1', marginRight: 4 }} />{asset.employee_name || asset.assigned_to_employee_id || '—'}</>)}
                  <Divider style={{ margin: '4px 0' }} />
                  {renderQuickInfoRow('Tài sản cha:', asset.parent_asset_name || asset.parent_asset_id || '—')}
                  {asset.accessories && asset.accessories.length > 0 && (
                    <>
                      <Divider style={{ margin: '4px 0' }} />
                      {renderQuickInfoRow(<><AppstoreAddOutlined style={{ marginRight: 4, color: '#52c41a' }} />Thiết bị/Vật tư:</>, <Text style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 13 }}>{asset.accessories.length} mục</Text>)}
                    </>
                  )}
                </Space>
              </Card>
              {/* Maintenance Info */}
              {asset.maintenance_schedules && asset.maintenance_schedules.length > 0 && (
                <Card style={cardStyle}>
                  <Title level={4} style={cardTitleStyle}><ClockCircleOutlined style={{ marginRight: 8, color: '#fa8c16' }} />Thông tin bảo dưỡng</Title>
                  <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    {asset.maintenance_schedules.map((schedule: any, index: number) => (
                      <div key={index} style={{ padding: '4px 0' }}>
                        <div style={{ marginBottom: 2 }}>
                          <Text strong style={{ fontSize: 13, color: '#1890ff' }}>
                            {schedule.maintenance_type_name || `Loại ${schedule.maintenance_type_id}`}
                          </Text>
                        </div>
                        <div>
                          <Text style={{ fontSize: 12, color: '#666' }}>
                            Chu kỳ: <Text strong>{schedule.cycle_months} tháng</Text>
                          </Text>
                        </div>
                        {schedule.notes && (
                          <div style={{ marginTop: 2 }}>
                            <Text style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                              {schedule.notes}
                            </Text>
                          </div>
                        )}
                        {index < asset.maintenance_schedules.length - 1 && (
                          <Divider style={{ margin: '4px 0' }} />
                        )}
                      </div>
                    ))}
                  </Space>
                </Card>
              )}
            </Space>
          </Col>
        </Row>
        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 32, color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>
          Phần mềm được phát triển bởi Tt - Version 1.0.<br/>
          Bản quyền thuộc về Công ty Đông Dương. © 2025. Mọi quyền được bảo lưu.
        </div>
      </div>
    </div>
  );
};

export default PublicAssetDetail; 